// Chips removíveis dos filtros LEGADOS aplicados por deep-link
// (Dashboard: status/channel/provider/startDate…). Esses filtros
// saíram da barra da Consulta simplificada, mas o contrato/URL
// continua aceitando-os: quando presentes, aparecem aqui de forma
// discreta ("Filtros aplicados: Falha ×, EMAIL ×") — remover o
// chip tira o filtro da URL.
import { XIcon } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { useNotificationCenterNotificationsSearchParams } from "@/hooks"
import type { NotificationSearchLegacyChip } from "@/hooks/use-notification-center-notifications-search-params"

import {
  getMacroStatusFromRawStatus,
  NOTIFICATION_MACRO_STATUSES,
} from "../constants"

const searchPrefix = "pages.notificationCenter.searchPage"

export const NotificationSearchAppliedChips = () => {
  const { t } = useTranslation()
  const {
    legacyChips,
    removeLegacyChip,
    isAllDates,
    resetToToday,
  } = useNotificationCenterNotificationsSearchParams()

  if (legacyChips.length === 0 && !isAllDates) return null

  // Rótulo do chip sintético "Todas as datas" (period=all).
  const allDatesLabel = t(`${searchPrefix}.allDatesChip`)

  // Rótulo humano do chip: status usa a tradução do macro-status
  // (aceita macro OU bruto), origem usa o grupo traduzido; canal,
  // provider e período mostram o próprio valor.
  const getChipLabel = (
    chip: NotificationSearchLegacyChip
  ) => {
    if (chip.key === "status") {
      const macroStatus = (
        NOTIFICATION_MACRO_STATUSES as readonly string[]
      ).includes(chip.value)
        ? chip.value
        : getMacroStatusFromRawStatus(chip.value)
      return macroStatus
        ? t(`${searchPrefix}.macroStatus.${macroStatus}`)
        : chip.value
    }
    if (chip.key === "originType") {
      return t(
        `${searchPrefix}.filters.originGroup.${chip.value}`
      )
    }
    if (chip.key === "templateUuid") {
      return t(`${searchPrefix}.appliedFilters.template`, {
        value: chip.value,
      })
    }
    return chip.value
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs text-muted-foreground">
        {t(`${searchPrefix}.appliedFilters.title`)}:
      </span>
      {isAllDates && (
        // Modo "todas as datas" (KPIs "Em fila"/"Agendados"):
        // remover volta pro dia default de hoje (limpa period).
        <Badge
          key="all-dates"
          variant="outline"
          className="gap-1 rounded-full pr-1 font-medium"
        >
          {allDatesLabel}
          <button
            type="button"
            onClick={resetToToday}
            aria-label={t(
              `${searchPrefix}.appliedFilters.remove`,
              { label: allDatesLabel }
            )}
            className="rounded-full p-0.5 hover:bg-accent"
          >
            <XIcon className="size-3" aria-hidden="true" />
          </button>
        </Badge>
      )}
      {legacyChips.map((chip) => {
        const label = getChipLabel(chip)
        return (
          <Badge
            key={chip.key}
            variant="outline"
            className="gap-1 rounded-full pr-1 font-medium"
          >
            {label}
            <button
              type="button"
              onClick={() => removeLegacyChip(chip.key)}
              aria-label={t(
                `${searchPrefix}.appliedFilters.remove`,
                { label }
              )}
              className="rounded-full p-0.5 hover:bg-accent"
            >
              <XIcon
                className="size-3"
                aria-hidden="true"
              />
            </button>
          </Badge>
        )
      })}
    </div>
  )
}
